"use client";

import { c as _c } from "react/compiler-runtime";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import { motion } from "framer-motion";
import type { FC } from "react";
import AdditionalMediaUpload from "../components/AdditionalMediaUpload";
import AirdropConfig from "../components/AirdropConfig";
import AllowlistBatchManager, { AllowlistBatchRaw } from "../components/AllowlistBatchManager";
import PaymentConfig from "../components/PaymentConfig";
import { AIRDROP_TOTAL, AirdropEntry, PaymentInfo } from "../types/OperationalData";
import { validateStrictAddress } from "../utils/addressValidation";
import { validateTokenIdFormat } from "../utils/tokenParsing";
interface AdditionalInfoStepProps {
  readonly airdropEntries: AirdropEntry[];
  readonly onAirdropEntriesChange: (entries: AirdropEntry[]) => void;
  readonly paymentInfo: PaymentInfo;
  readonly onPaymentInfoChange: (paymentInfo: PaymentInfo) => void;
  readonly allowlistBatches: AllowlistBatchRaw[];
  readonly supportingMedia: string[];
  readonly artworkCommentary: string;
  readonly aboutArtist: string;
  readonly previewImage: string;
  readonly promoVideo: string;
  readonly requiresPreviewImage: boolean;
  readonly requiresPromoVideoOption: boolean;
  readonly previewRequiredMediaType: string | null;
  readonly onBatchesChange: (batches: AllowlistBatchRaw[]) => void;
  readonly onSupportingMediaChange: (media: string[]) => void;
  readonly onPreviewImageChange: (url: string) => void;
  readonly onPromoVideoChange: (url: string) => void;
  readonly onArtworkCommentaryChange: (commentary: string) => void;
  readonly onAboutArtistChange: (aboutArtist: string) => void;
  readonly onBack: () => void;
  readonly onSubmit: () => void;
  readonly isSubmitting: boolean;
}
const AdditionalInfoStep: FC<AdditionalInfoStepProps> = t0 => {
  const $ = _c(57);
  const {
    airdropEntries,
    onAirdropEntriesChange,
    paymentInfo,
    onPaymentInfoChange,
    allowlistBatches,
    supportingMedia,
    artworkCommentary,
    aboutArtist,
    previewImage,
    promoVideo,
    requiresPreviewImage,
    requiresPromoVideoOption,
    previewRequiredMediaType,
    onBatchesChange,
    onSupportingMediaChange,
    onPreviewImageChange,
    onPromoVideoChange,
    onArtworkCommentaryChange,
    onAboutArtistChange,
    onBack,
    onSubmit,
    isSubmitting
  } = t0;
  const getContractError = _temp;
  const getTokenIdsError = _temp2;
  let t1;
  if ($[0] !== aboutArtist || $[1] !== airdropEntries || $[2] !== allowlistBatches || $[3] !== artworkCommentary || $[4] !== paymentInfo || $[5] !== previewImage || $[6] !== requiresPreviewImage) {
    t1 = () => {
      const hasInvalidCount = airdropEntries.some(_temp3);
      if (hasInvalidCount) {
        return false;
      }
      const totalAllocated = airdropEntries.reduce(_temp4, 0);
      if (totalAllocated !== AIRDROP_TOTAL) {
        return false;
      }
      const hasEntryWithCountButNoValidAddress = airdropEntries.some(_temp5);
      if (hasEntryWithCountButNoValidAddress) {
        return false;
      }
      const hasEntryWithAddressButNoCount = airdropEntries.some(_temp6);
      if (hasEntryWithAddressButNoCount) {
        return false;
      }
      const hasInvalidAddress = airdropEntries.some(_temp7);
      if (hasInvalidAddress) {
        return false;
      }
      if (!paymentInfo.payment_address || !validateStrictAddress(paymentInfo.payment_address)) {
        return false;
      }
      const hasInvalidBatch = allowlistBatches.some(_temp8);
      if (hasInvalidBatch) {
        return false;
      }
      const hasInvalidTokenIds = allowlistBatches.some(_temp9);
      if (hasInvalidTokenIds) {
        return false;
      }
      if (!artworkCommentary.trim()) {
        return false;
      }
      if (!aboutArtist.trim()) {
        return false;
      }
      if (requiresPreviewImage && !previewImage) {
        return false;
      }
      return true;
    };
    $[0] = aboutArtist;
    $[1] = airdropEntries;
    $[2] = allowlistBatches;
    $[3] = artworkCommentary;
    $[4] = paymentInfo;
    $[5] = previewImage;
    $[6] = requiresPreviewImage;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const isFormValid = t1;
  let t2;
  let t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 20
    };
    t3 = {
      opacity: 1,
      y: 0
    };
    t4 = {
      opacity: 0,
      y: -20
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="tw-text-sm tw-text-iron-400">Complete the following details for distribution and storytelling purposes.</p>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== airdropEntries || $[13] !== onAirdropEntriesChange) {
    t6 = <AirdropConfig entries={airdropEntries} onEntriesChange={onAirdropEntriesChange} />;
    $[12] = airdropEntries;
    $[13] = onAirdropEntriesChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== onPaymentInfoChange || $[16] !== paymentInfo) {
    t7 = <PaymentConfig paymentInfo={paymentInfo} onPaymentInfoChange={onPaymentInfoChange} />;
    $[15] = onPaymentInfoChange;
    $[16] = paymentInfo;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== allowlistBatches) {
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = batch_1 => {
        const contractError = getContractError(batch_1.contract);
        const tokenIdsError = getTokenIdsError(batch_1.token_ids_raw);
        return {
          ...(contractError ? {
            contract: contractError
          } : {}),
          ...(tokenIdsError ? {
            token_ids: tokenIdsError
          } : {})
        };
      };
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = allowlistBatches.map(t9);
    $[18] = allowlistBatches;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[21] !== allowlistBatches || $[22] !== onBatchesChange || $[23] !== t8) {
    t9 = <AllowlistBatchManager batches={allowlistBatches} onBatchesChange={onBatchesChange} errors={t8} />;
    $[21] = allowlistBatches;
    $[22] = onBatchesChange;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== aboutArtist || $[26] !== artworkCommentary || $[27] !== onAboutArtistChange || $[28] !== onArtworkCommentaryChange || $[29] !== onPreviewImageChange || $[30] !== onPromoVideoChange || $[31] !== onSupportingMediaChange || $[32] !== previewImage || $[33] !== previewRequiredMediaType || $[34] !== promoVideo || $[35] !== requiresPreviewImage || $[36] !== requiresPromoVideoOption || $[37] !== supportingMedia) {
    t10 = <AdditionalMediaUpload supportingMedia={supportingMedia} artworkCommentary={artworkCommentary} aboutArtist={aboutArtist} previewImage={previewImage} promoVideo={promoVideo} requiresPreviewImage={requiresPreviewImage} requiresPromoVideoOption={requiresPromoVideoOption} previewRequiredMediaType={previewRequiredMediaType} onSupportingMediaChange={onSupportingMediaChange} onPreviewImageChange={onPreviewImageChange} onPromoVideoChange={onPromoVideoChange} onArtworkCommentaryChange={onArtworkCommentaryChange} onAboutArtistChange={onAboutArtistChange} />;
    $[25] = aboutArtist;
    $[26] = artworkCommentary;
    $[27] = onAboutArtistChange;
    $[28] = onArtworkCommentaryChange;
    $[29] = onPreviewImageChange;
    $[30] = onPromoVideoChange;
    $[31] = onSupportingMediaChange;
    $[32] = previewImage;
    $[33] = previewRequiredMediaType;
    $[34] = promoVideo;
    $[35] = requiresPreviewImage;
    $[36] = requiresPromoVideoOption;
    $[37] = supportingMedia;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  let t11;
  if ($[39] !== t10 || $[40] !== t6 || $[41] !== t7 || $[42] !== t9) {
    t11 = <div className="tw-flex-1 tw-space-y-12 tw-overflow-y-auto tw-px-4 tw-py-2 tw-scrollbar-thin tw-scrollbar-track-iron-900 tw-scrollbar-thumb-iron-700">{t5}{t6}{t7}{t9}{t10}</div>;
    $[39] = t10;
    $[40] = t6;
    $[41] = t7;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== isSubmitting || $[45] !== onBack) {
    t12 = <SecondaryButton onClicked={onBack} disabled={isSubmitting}>Back</SecondaryButton>;
    $[44] = isSubmitting;
    $[45] = onBack;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  const t13 = !isFormValid();
  let t14;
  if ($[47] !== isSubmitting || $[48] !== onSubmit || $[49] !== t13) {
    t14 = <PrimaryButton onClicked={onSubmit} disabled={t13} loading={isSubmitting} padding="tw-px-6 tw-py-3">Submit Artwork</PrimaryButton>;
    $[47] = isSubmitting;
    $[48] = onSubmit;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== t12 || $[52] !== t14) {
    t15 = <div className="tw-mt-auto tw-flex tw-items-center tw-justify-between tw-border-t tw-border-iron-800 tw-px-4 tw-pt-6">{t12}{t14}</div>;
    $[51] = t12;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== t11 || $[55] !== t15) {
    t16 = <motion.div initial={t2} animate={t3} exit={t4} className="tw-mx-auto tw-flex tw-h-full tw-max-w-4xl tw-flex-col tw-pb-8">{t11}{t15}</motion.div>;
    $[54] = t11;
    $[55] = t15;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  return t16;
};
export default AdditionalInfoStep;
function _temp(address) {
  if (!address) {
    return;
  }
  if (!validateStrictAddress(address)) {
    return "Invalid contract address";
  }
}
function _temp2(tokenIds) {
  return validateTokenIdFormat(tokenIds) || undefined;
}
function _temp3(e) {
  return !Number.isFinite(e.count) || e.count < 0 || !Number.isInteger(e.count);
}
function _temp4(sum, e_0) {
  return sum + (e_0.count ?? 0);
}
function _temp5(e_1) {
  return e_1.count > 0 && (!e_1.address || !validateStrictAddress(e_1.address));
}
function _temp6(e_2) {
  return e_2.address && validateStrictAddress(e_2.address) && (!e_2.count || e_2.count <= 0);
}
function _temp7(e_3) {
  return e_3.address && !validateStrictAddress(e_3.address);
}
function _temp8(batch) {
  return !batch.contract || !validateStrictAddress(batch.contract);
}
function _temp9(batch_0) {
  return validateTokenIdFormat(batch_0.token_ids_raw) !== null;
}